import ToolsTable from './ToolsTable';
import { PortalToolOut } from '../types/apiRequests';

interface SchemaProp {
  type?: string;
  description?: string;
}

interface McpInputSchema {
  properties?: Record<string, SchemaProp>;
  required?: string[];
}

interface ToolParameter {
  name: string;
  type: string;
  required: boolean;
  description: string;
}

// A workflow exposed as an MCP tool is invoked via a JSON-RPC POST to the workflows MCP
// endpoint; there is no per-tool HTTP verb, so we present POST consistently (matching the
// server Tools page, which always shows the method + path).
const MCP_METHOD = 'POST';

// Map each MCP-trigger node to the PortalToolOut shape the server Tools page renders, so the
// workflow Tools tab reuses ToolsTable verbatim (method badge, path, expandable parameters).
function buildTools(nodes: Record<string, unknown>[]): PortalToolOut[] {
  const tools: PortalToolOut[] = [];
  (nodes || []).forEach((node, index) => {
    const data = (node.data as Record<string, unknown>) || {};
    if (data.type !== 'trigger' || data.triggerType !== 'mcp') return;
    const schema = (data.mcpToolInputSchema as McpInputSchema) || {};
    const properties = schema.properties || {};
    const required = new Set(schema.required || []);
    const name = (data.mcpToolName as string) || (data.label as string) || `mcp-tool-${index}`;
    const parameters: ToolParameter[] = Object.entries(properties).map(([key, def]) => ({
      name: key,
      type: def?.type || 'string',
      required: required.has(key),
      description: def?.description || '',
    }));
    tools.push({
      // Suffix with index so two MCP-trigger nodes sharing a tool name don't collide as React keys.
      id: `${name}-${index}`,
      name,
      description: (data.mcpToolDescription as string) || '',
      http_method: MCP_METHOD,
      path: `/${name}`,
      parameters,
      tag: null,
    });
  });
  return tools;
}

export default function WorkflowToolsTab({
  nodes,
  workflowName,
}: {
  nodes: Record<string, unknown>[];
  workflowName: string;
}) {
  const tools = buildTools(nodes);
  // Single group keyed by the workflow's name, mirroring the server page's tag grouping.
  const toolsByTag: Record<string, PortalToolOut[]> = tools.length > 0 ? { [workflowName]: tools } : {};
  return <ToolsTable tools={tools} toolsByTag={toolsByTag} />;
}
